// libs
import { useContext, useCallback, useMemo } from "react";

// internal-deps
import { AnalyticsContext, NEWRELIC_TABLES, recordNREvent } from "@swiggy-private/checkout-core";
import {
    SECURE_CARD_INFO_ACTION,
    ADD_NEW_CARD_PROCEED_ACTION_STATUS,
    NCEMIEvents,
    AddNewCardProceedMeta,
} from "@swiggy-private/payment-ui";

// analytics
import {
    onCheckboxSecureCardClickEvent,
    onTokenisationHalfCardScreenViewEvent,
    onKnowMoreTokenizationClickEvent,
    onClickOptionsClickEvent,
    onProceedToPayClickEvent,
    skipCVVImpressionEvent,
    onCardNotEligibleEmiCroutonImpressionEvent,
    onPayInEMIClickEvent,
} from "@payment-client/analytics/events/Card";

// utils
import { GlobalHeaderState, SwgyHeaderKeysList } from "@swiggy-private/checkout-core";

const TOKENIZATION = "tokenization";

interface CardEventsReturnType {
    onSaveCardClick: (value: boolean) => void;
    onSecureCardInfoView: VoidFunction;
    onHideCvv: VoidFunction;
    onSecureCardInfoClickAction: (action: SECURE_CARD_INFO_ACTION) => void;
    onProceedToPayClick: (actionStatus: ADD_NEW_CARD_PROCEED_ACTION_STATUS, meta?: AddNewCardProceedMeta) => void;
    onKnowMoreTokenizationClickAction?: VoidFunction;
    onProceedToPayClickAction: (secureCard: boolean, discountText: string) => void;
    onCardNotEligibleCroutonImpression: (message: string, emiPage?: boolean) => void;
    NCEMIEvents: NCEMIEvents;
}

const useCardEvents = (screenName: string): CardEventsReturnType => {
    const { clickEvent, screenViewEvent, impressionEvent } = useContext(AnalyticsContext);
    const cartId = GlobalHeaderState.getHeader(SwgyHeaderKeysList.cartId) ?? "";
    const businessLine = GlobalHeaderState.getHeader(SwgyHeaderKeysList.marketplaceBusinessLine) ?? "";

    const onSaveCardClick = useCallback(
        (isChecked: boolean) => {
            onCheckboxSecureCardClickEvent(screenName, isChecked ? 1 : 0, isChecked ? "true" : "false", clickEvent);
        },
        [clickEvent, screenName],
    );

    const onSecureCardInfoClickAction = useCallback(
        (context: string) => {
            onClickOptionsClickEvent(screenName, context, TOKENIZATION, clickEvent);
        },
        [clickEvent, screenName],
    );

    const onSecureCardInfoView = useCallback(() => {
        onTokenisationHalfCardScreenViewEvent(screenName, screenViewEvent);
    }, [screenName, screenViewEvent]);

    const onHideCvv = useCallback(() => {
        skipCVVImpressionEvent(screenName, impressionEvent);
    }, [screenName, impressionEvent]);

    const onKnowMoreTokenizationClickAction = useCallback(() => {
        onKnowMoreTokenizationClickEvent(screenName, clickEvent);
    }, [clickEvent, screenName]);

    const onProceedToPayClick = useCallback(
        (actionStatus: ADD_NEW_CARD_PROCEED_ACTION_STATUS, meta?: AddNewCardProceedMeta) => {
            recordNREvent({
                eventType: NEWRELIC_TABLES.WP_ADD_NEW_CARD_PROCEED_TO_PAY,
                actionStatus,
                screenName,
                cardBrand: meta?.cardBrand ?? "--",
                skipClientCardValidation: String(meta?.skipClientCardValidation ?? false),
            });
        },
        [screenName],
    );

    const onProceedToPayClickAction = useCallback(
        (secureCard: boolean, discountText: string) => {
            onProceedToPayClickEvent(
                screenName,
                JSON.stringify({
                    secureCard: secureCard,
                    businessLine: businessLine,
                    cartId: cartId,
                    discountText: discountText,
                }),
                clickEvent,
            );
        },
        [clickEvent, screenName],
    );

    // crouton events
    const onCardNotEligibleCroutonImpression = useCallback(
        (message: string, emiPage = false) => {
            onCardNotEligibleEmiCroutonImpressionEvent(impressionEvent, message, emiPage);
        },
        [impressionEvent],
    );

    const onPayInEMIClickAction = useCallback(() => {
        onPayInEMIClickEvent(screenName, clickEvent);
    }, [clickEvent, screenName]);

    const NCEMIEvents = useMemo(() => {
        return {
            onPayInEMIClickAction,
        };
    }, [onPayInEMIClickAction]);

    return {
        onSaveCardClick,
        onSecureCardInfoClickAction,
        onSecureCardInfoView,
        onHideCvv,
        onKnowMoreTokenizationClickAction,
        onProceedToPayClick,
        onProceedToPayClickAction,
        onCardNotEligibleCroutonImpression,
        NCEMIEvents,
    };
};

export default useCardEvents;
